import { RunDto, RunResultsHistoricAggregate } from "@custom-types/modelRun"
import ChartDataLabels from "chartjs-plugin-datalabels"
import zoomPlugin from "chartjs-plugin-zoom"
import { Bar } from "react-chartjs-2"
import { useMemo, useRef, useState } from "react"
import DownloadChartImageDialog from "@features/results/DownloadChartImageDialog"
import { Box, Button, CircularProgress, Grid, Typography } from "@mui/material"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import BarStringsChartOptionsDropdown, {
  BAR_STRINGS_CHART_OPTIONS_KEY,
  BarStringsChartOptions,
  defaultBarStringsChartOptions,
} from "@features/results/Result/ChartsContainer/BarStringsChartOptionsDropdown"
import { useLocalStorage } from "usehooks-ts"
import { useChartDensity } from "@features/results/Result/ChartsContainer/useChartDensity"
import { useBarStringsChartData } from "@features/results/Result/ChartsContainer/useBarStringsChartData"
import { createBarStringsChartOptions } from "@features/results/Result/ChartsContainer/BarStringsChart.utils"
import { Chart } from "chart.js"
import { BarStringsChartTable } from "@features/results/Result/ChartsContainer/BarStringsChartTable"
import ShowChartAsTableButton from "@features/results/Result/ShowChartAsTableButton"

export type BarStringsChartProps = {
  aggregateData: RunResultsHistoricAggregate | undefined
  nodes: GroupNodeLabel[] | null
  lagShift: number
  isLoading?: boolean
  runs: RunDto[]
}

const MAX_BARS_FOR_DEFAULT_LABELS = 100
const CATEGORICAL_TABLE_FILE_NAME = "categorical-samples-table"

export const BarStringsChart = ({
  aggregateData,
  nodes,
  lagShift,
  isLoading,
  runs,
}: BarStringsChartProps) => {
  const chartContainerRef = useRef<HTMLDivElement | null>(null)
  const chartRef = useRef<Chart<"bar">>(null)
  const [showAsTable, setShowAsTable] = useState(false)

  const [chartOptions] = useLocalStorage<BarStringsChartOptions>(
    BAR_STRINGS_CHART_OPTIONS_KEY,
    defaultBarStringsChartOptions,
  )

  const { timestepLabels, chartData, dataSets } = useBarStringsChartData({
    aggregateData,
    lagShift,
    runs,
    isStacked: chartOptions.isStacked,
  })

  const { defaultShowLabels, shouldShowLabels } = useChartDensity({
    datasets: chartData.datasets,
    showLabels: chartOptions.showLabels,
    maxBarsForDefaultLabels: MAX_BARS_FOR_DEFAULT_LABELS,
  })

  const options = useMemo(
    () => createBarStringsChartOptions(chartOptions.isStacked, timestepLabels, shouldShowLabels),
    [chartOptions.isStacked, shouldShowLabels, timestepLabels],
  )

  const runReference = useMemo(() => runs.map((run) => run.key).join(" vs. "), [runs])

  const nodesLabel = useMemo(
    () =>
      nodes && nodes.length > 1
        ? nodes.map((node) => node.label).join(", ")
        : nodes?.[0]?.label || "",
    [nodes],
  )

  const handleResetZoom = () => {
    if (chartRef && chartRef.current) {
      chartRef.current.resetZoom()
    }
  }

  return (
    <>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid item xs={12}>
            <Box ref={chartContainerRef}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {!showAsTable && (
                  <DownloadChartImageDialog
                    buttonProps={{
                      sx: { ml: 1 },
                    }}
                    chartRef={chartContainerRef}
                    defaultFileName="Time-series chart"
                    runReference={runReference}
                  />
                )}
                <Button onClick={handleResetZoom} variant="contained" sx={{ ml: 1 }}>
                  Reset zoom
                </Button>
                <BarStringsChartOptionsDropdown defaultShowLabels={defaultShowLabels} />
                <ShowChartAsTableButton
                  showAsTable={showAsTable}
                  onClick={() => setShowAsTable((value) => !value)}
                />
              </Box>
              <Typography variant="h6" textAlign="center" sx={{ mb: 1 }}>
                100% {chartOptions.isStacked ? "Stacked" : "Grouped"} Bar Chart <b>{nodesLabel}</b>
              </Typography>
              {showAsTable ? (
                <BarStringsChartTable
                  dataSets={dataSets}
                  timestepLabels={timestepLabels}
                  exportFileName={CATEGORICAL_TABLE_FILE_NAME}
                />
              ) : (
                <Bar
                  options={options}
                  plugins={[ChartDataLabels, zoomPlugin]}
                  data={chartData}
                  height={100}
                  ref={chartRef}
                />
              )}
            </Box>
          </Grid>
        </>
      )}
    </>
  )
}
